import useLatestRead from '../../hooks/useLatestRead';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { generationsAPI, projectsAPI, promptTemplatesAPI } from '../../services/api';
import {
  DEFAULT_IMAGE_MODEL,
  IMAGE_MODEL_OPTIONS,
  getImageCost,
  normalizeImageModel,
} from '../../services/imageModels';
import { useAuth } from '../../context/AuthContext';
import OptimizedImage, { getPreviewImagePath } from '../OptimizedImage';
import './GenerationMenu.css';

const GenerationMenu = ({
  projectId,
  project,
  onGenerationComplete,
  onPlaceholdersCreated,
  onGenerationUpdate,
  onRetryStatus,
  prefillData
}) => {
  const aspectRatioOptions = [
    { value: '4:5', label: '4:5 Post' },
    { value: '9:16', label: '9:16 Stories' },
    { value: '1:1', label: '1:1 Post' },
    { value: '16:9', label: '16:9 Wide' },
  ];

  const toAspectRatioValue = (value) => {
    const option = aspectRatioOptions.find(
      (opt) => opt.value === value || opt.label === value || value === `${opt.value} Post` || value === `${opt.value} Stories`
    );
    return option ? option.value : aspectRatioOptions[0].value;
  };

  const normalizeAspectRatio = (value) => {
    const option = aspectRatioOptions.find(
      (opt) => opt.value === value || opt.label === value || value === `${opt.value} Post` || value === `${opt.value} Stories`
    );
    return option ? option.label : aspectRatioOptions[0].label;
  };

  const startReferenceRead = useLatestRead();
  const [prompt, setPrompt] = useState('');
  const [imageReferences, setImageReferences] = useState([]);
  const [projectReferences, setProjectReferences] = useState([]);
  const [selectedProjectReferences, setSelectedProjectReferences] = useState([]);
  const [hasCustomSelection, setHasCustomSelection] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(normalizeAspectRatio('4:5'));
  const [generationModel, setGenerationModel] = useState(DEFAULT_IMAGE_MODEL);
  const [resolution, setResolution] = useState('4k');
  const [outputFormat, setOutputFormat] = useState('png');
  const [quantity, setQuantity] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const projectReferenceInputRef = useRef(null);
  const promptTemplatesRef = useRef(null);
  const promptInputRef = useRef(null);
  const promptHeightRef = useRef(0);
  const hasStoredProjectSettingsRef = useRef(false);
  const [showPromptTemplates, setShowPromptTemplates] = useState(false);
  const [promptTemplates, setPromptTemplates] = useState([]);
  const [isAddingTemplate, setIsAddingTemplate] = useState(false);
  const [newTemplateText, setNewTemplateText] = useState('');
  const buttonTimeoutRef = useRef(null);
  const oneTimeReferenceInputRef = useRef(null);
  const imageReferencePreviewsRef = useRef([]);
  const [imageReferencePreviews, setImageReferencePreviews] = useState([]);
  const [prefilledReferences, setPrefilledReferences] = useState([]);
  const { user } = useAuth();

  const projectSettingsStorageKey = useMemo(() => {
    if (!user || !projectId) return null;
    return `generationSettings:user:${user.id}:project:${projectId}`;
  }, [user, projectId]);

  useEffect(() => {
    loadPromptTemplates();

    // Cleanup timeout on unmount
    return () => {
      if (buttonTimeoutRef.current) {
        clearTimeout(buttonTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    imageReferencePreviewsRef.current = imageReferencePreviews;
  }, [imageReferencePreviews]);

  useEffect(() => () => {
    imageReferencePreviewsRef.current.forEach((preview) => {
      if (preview.url) {
        URL.revokeObjectURL(preview.url);
      }
    });
  }, []);

  // Close prompt templates dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (promptTemplatesRef.current && !promptTemplatesRef.current.contains(event.target)) {
        setShowPromptTemplates(false);
        setIsAddingTemplate(false);
        setNewTemplateText('');
      }
    };

    if (showPromptTemplates) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPromptTemplates]);

  // Load project settings and references when projectId changes
  useEffect(() => {
    if (!projectId || !project || !user) return;

    let storedSettings = null;
    if (projectSettingsStorageKey) {
      try {
        storedSettings = JSON.parse(localStorage.getItem(projectSettingsStorageKey)) || null;
      } catch (err) {
        console.warn('Failed to parse stored generation settings:', err);
      }
    }
    hasStoredProjectSettingsRef.current = !!storedSettings;

    const storedAspectRatio = storedSettings?.aspectRatio || project.last_aspect_ratio;
    const storedResolution = storedSettings?.resolution || project.last_resolution;
    const storedOutputFormat = storedSettings?.outputFormat || project.last_output_format;
    const storedQuantity = storedSettings?.quantity;
    const storedPrompt = storedSettings?.prompt;
    const storedGenerationModel = storedSettings?.generationModel;
    const storedSelectedRefs = Array.isArray(storedSettings?.selectedProjectReferences)
      ? storedSettings.selectedProjectReferences
      : [];

    if (storedAspectRatio) setAspectRatio(normalizeAspectRatio(storedAspectRatio));
    if (storedGenerationModel) setGenerationModel(normalizeImageModel(storedGenerationModel));
    if (storedResolution) setResolution(storedResolution);
    if (storedOutputFormat) setOutputFormat(storedOutputFormat);
    if (typeof storedQuantity === 'number' && storedQuantity > 0) setQuantity(storedQuantity);
    if (typeof storedPrompt === 'string') setPrompt(storedPrompt);

    setHasCustomSelection(storedSelectedRefs.length > 0);
    setSelectedProjectReferences(storedSelectedRefs);

    // Load project references; if we already have stored selections, keep them, otherwise select all
    loadProjectReferences({ forceSelectAll: storedSelectedRefs.length === 0, initialSelectedIds: storedSelectedRefs });
  }, [projectId, project, user, projectSettingsStorageKey]); // Only reset when projectId changes, not when project data refreshes

  const loadProjectReferences = async ({ forceSelectAll = false, initialSelectedIds } = {}) => {
    if (!projectId) {
      setProjectReferences([]);
      setSelectedProjectReferences([]);
      return;
    }
    const read = startReferenceRead();
    try {
      const response = await projectsAPI.getReferences(projectId, { signal: read.signal });
      if (!read.isCurrent()) return;
      const references = Array.isArray(response.data?.references)
        ? response.data.references
        : Array.isArray(response.data)
          ? response.data
          : [];

      setProjectReferences(references);

      const referenceIds = references.map((ref) => ref.id);
      setSelectedProjectReferences((prevSelected) => {
        const baseSelected = Array.isArray(initialSelectedIds) ? initialSelectedIds : prevSelected;
        const shouldSelectAll = forceSelectAll || (!hasCustomSelection && !initialSelectedIds);
        if (shouldSelectAll) {
          return referenceIds;
        }
        const selectedSet = new Set(baseSelected);
        return referenceIds.filter((id) => selectedSet.has(id));
      });
    } catch (err) {
      if (!read.isCurrent()) return;
      console.error('Failed to load project references:', err);
      setError('Failed to load project references');
    }
  };

  useEffect(() => {
    if (!projectSettingsStorageKey) return;
    const payload = {
      prompt,
      aspectRatio,
      generationModel,
      resolution,
      outputFormat,
      quantity,
      selectedProjectReferences
    };
    try {
      localStorage.setItem(projectSettingsStorageKey, JSON.stringify(payload));
    } catch (err) {
      console.warn('Failed to persist generation settings:', err);
    }
  }, [projectSettingsStorageKey, prompt, aspectRatio, generationModel, resolution, outputFormat, quantity, selectedProjectReferences]);

  const handleImageReferenceChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      // Append new images to existing ones instead of replacing
      setImageReferences(prev => [...prev, ...filesArray]);
      setImageReferencePreviews(prev => [
        ...prev,
        ...filesArray.map((file) => ({
          url: URL.createObjectURL(file),
          name: file.name,
          selected: true // New images are selected by default
        }))
      ]);
      if (oneTimeReferenceInputRef.current) {
        oneTimeReferenceInputRef.current.value = '';
      }
    }
  };

  const toggleImageReferenceSelection = (index) => {
    setImageReferencePreviews(prev =>
      prev.map((preview, i) =>
        i === index ? { ...preview, selected: !preview.selected } : preview
      )
    );
  };

  const removeImageReference = (index) => {
    setImageReferences(prev => prev.filter((_, i) => i !== index));
    setImageReferencePreviews((prev) => {
      const removed = prev[index];
      if (removed?.url) {
        URL.revokeObjectURL(removed.url);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleUploadProjectReference = async (e) => {
    if (!projectId || !e.target.files || !e.target.files[0]) return;

    const formData = new FormData();
    formData.append('image', e.target.files[0]);

    try {
      await projectsAPI.uploadReference(projectId, formData);
      await loadProjectReferences();
      setError('');
      // Reset the file input
      if (projectReferenceInputRef.current) {
        projectReferenceInputRef.current.value = '';
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload reference image');
    }
  };

  const handleClickUploadReference = () => {
    if (projectReferenceInputRef.current) {
      projectReferenceInputRef.current.click();
    }
  };

  const handleClickOneTimeReference = () => {
    if (oneTimeReferenceInputRef.current) {
      oneTimeReferenceInputRef.current.click();
    }
  };

  const removePrefilledReference = (index) => {
    setPrefilledReferences((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCopyPrompt = async () => {
    if (!prompt.trim()) return;
    try {
      await navigator.clipboard.writeText(prompt);
    } catch (err) {
      console.error('Failed to copy prompt:', err);
    }
  };

  const handleToggleProjectReference = (refId) => {
    setHasCustomSelection(true);
    setSelectedProjectReferences((prevSelected) => (
      prevSelected.includes(refId)
        ? prevSelected.filter((id) => id !== refId)
        : [...prevSelected, refId]
    ));
  };

  const handleDeleteProjectReference = async (refId) => {
    if (!window.confirm('Delete this reference image?')) return;
    try {
      await projectsAPI.deleteReference(projectId, refId);
      await loadProjectReferences();
    } catch (err) {
      setError('Failed to delete reference image');
    }
  };

  const handleAddTemplate = () => {
    if (!newTemplateText.trim()) return;

    promptTemplatesAPI.create(newTemplateText.trim())
      .then((response) => {
        setPromptTemplates((prevTemplates) => [response.data, ...prevTemplates]);
        setNewTemplateText('');
        setIsAddingTemplate(false);
      })
      .catch(() => {
        setError('Failed to save prompt template');
      });
  };

  const handleUseTemplate = (templateText) => {
    setPrompt(prev => prev ? prev + ' ' + templateText : templateText);
  };

  const handleDeleteTemplate = (templateId) => {
    promptTemplatesAPI.delete(templateId)
      .then(() => {
        setPromptTemplates((prevTemplates) => prevTemplates.filter(t => t.id !== templateId));
      })
      .catch(() => {
        setError('Failed to delete prompt template');
      });
  };

  const loadPromptTemplates = async () => {
    try {
      const response = await promptTemplatesAPI.getAll();
      setPromptTemplates(response.data);
    } catch (err) {
      console.error('Failed to load prompt templates:', err);
    }
  };

  useEffect(() => {
    if (!prefillData || hasStoredProjectSettingsRef.current) return;

    if (prefillData.prompt) {
      setPrompt(prefillData.prompt);
    }
    if (prefillData.aspect_ratio) {
      setAspectRatio(normalizeAspectRatio(prefillData.aspect_ratio));
    }
    if (prefillData.resolution) {
      setResolution(prefillData.resolution);
    }
    if (prefillData.model) {
      setGenerationModel(normalizeImageModel(prefillData.model));
    }
    if (prefillData.output_format) {
      setOutputFormat(prefillData.output_format);
    }
    if (Array.isArray(prefillData.reference_images)) {
      setPrefilledReferences(prefillData.reference_images.map((ref, idx) => ({
        path: ref.reference_path || ref.image_path || ref.path || '',
        type: ref.reference_type || 'upload',
        thumbnail_path: ref.thumbnail_path,
        id: ref.id || idx
      })).filter((ref) => ref.path));
    }
  }, [prefillData]);

  useEffect(() => {
    // Resize prompt textarea to fit content, growing and shrinking as needed
    const el = promptInputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const newHeight = el.scrollHeight;
    el.style.height = `${newHeight}px`;
    promptHeightRef.current = newHeight;
  }, [prompt]);

  const handlePromptChange = (e) => {
    const { value } = e.target;
    setPrompt(value);

    const el = promptInputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const newHeight = el.scrollHeight;
    el.style.height = `${newHeight}px`;
    promptHeightRef.current = newHeight;
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) {
      setError('Prompt is required');
      return;
    }

    setIsGenerating(true);
    setError('');
    try {
      const { data } = await generationsAPI.getStorage();
      if (data.usedPercent >= data.thresholdPercent) {
        setError('Le stockage est plein. Supprimez des images avant de continuer à générer.');
        setIsGenerating(false);
        return;
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Impossible de vérifier le stockage. Réessayez plus tard.');
      setIsGenerating(false);
      return;
    }
    const safeAspectRatioValue = toAspectRatioValue(aspectRatio);
    const normalizedAspectRatioLabel = normalizeAspectRatio(aspectRatio);
    if (normalizedAspectRatioLabel !== aspectRatio) {
      setAspectRatio(normalizedAspectRatioLabel);
    }

    // Set button to re-enable after 3 seconds
    if (buttonTimeoutRef.current) {
      clearTimeout(buttonTimeoutRef.current);
    }
    buttonTimeoutRef.current = setTimeout(() => {
      setIsGenerating(false);
    }, 3000);

    // Create placeholders for each generation
    const placeholders = [];
    for (let i = 0; i < quantity; i++) {
      const placeholderId = `placeholder-${Date.now()}-${i}`;
      placeholders.push({
        id: placeholderId,
        isPlaceholder: true,
        prompt: prompt,
        aspect_ratio: normalizedAspectRatioLabel,
        model: generationModel,
        resolution: resolution,
        output_format: outputFormat,
        project_id: projectId ? parseInt(projectId) : null,
        project_name: project?.name || null,
        created_at: new Date().toISOString()
      });
    }

    // Notify parent about placeholders
    if (onPlaceholdersCreated) {
      onPlaceholdersCreated(placeholders);
    }

    try {
      const projectReferencePaths = projectReferences
        .filter((ref) => selectedProjectReferences.includes(ref.id))
        .map((ref) => ref.image_path)
        .filter(Boolean);
      const replicatedReferencePaths = prefilledReferences.map((ref) => ref.path).filter(Boolean);

      // Generate images individually and track their status
      const generationPromises = placeholders.map(async (placeholder, i) => {
        const formData = new FormData();
        formData.append('prompt', prompt);
        formData.append('aspectRatio', safeAspectRatioValue);
        formData.append('model', generationModel);
        formData.append('resolution', resolution);
        formData.append('outputFormat', outputFormat);
        if (projectId) {
          formData.append('projectId', projectId);
        }
        if (imageReferences.length > 0) {
          // Only send selected images
          imageReferences.forEach((file, index) => {
            if (imageReferencePreviews[index]?.selected) {
              formData.append('imageReferences', file);
            }
          });
        }
        if (projectReferencePaths.length > 0) {
          formData.append('projectReferencePaths', JSON.stringify(projectReferencePaths));
        }
        if (replicatedReferencePaths.length > 0) {
          formData.append('existingReferencePaths', JSON.stringify(replicatedReferencePaths));
        }

        try {
          await generationsAPI.createWithStream(formData, {
            onRetry: (retryData) => {
              // Notify parent about retry status
              if (onRetryStatus) {
                onRetryStatus(placeholder.id, retryData);
              }
            },
            onComplete: (data) => {
              // Notify parent about successful generation
              if (onGenerationUpdate) {
                onGenerationUpdate(placeholder.id, { success: true, data: data.data });
              }
            },
            onError: (errorData) => {
              // Notify parent about failed generation
              if (onGenerationUpdate) {
                onGenerationUpdate(placeholder.id, {
                  success: false,
                  error: errorData.error || 'Failed to generate image'
                });
              }
            },
          });
          return { success: true };
        } catch (err) {
          // Handle network/fetch errors
          let errorMessage = 'Failed to generate image';
          if (err.message) {
            errorMessage = err.message;
          }

          if (onGenerationUpdate) {
            onGenerationUpdate(placeholder.id, {
              success: false,
              error: errorMessage
            });
          }
          throw err;
        }
      });

      await Promise.allSettled(generationPromises);

      // Save settings to project if in project context
      if (projectId) {
        try {
          await projectsAPI.updateSettings(projectId, {
            aspectRatio: safeAspectRatioValue,
            resolution,
            outputFormat,
          });
        } catch (err) {
          console.error('Failed to save project settings:', err);
        }
      }

      // Keep image references after generation so they can be reused
      // setImageReferences([]);
      if (onGenerationComplete) {
        onGenerationComplete();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate image');
    }
  };

  return (
    <div className="generation-menu-wrapper">
      <div className="generation-menu">
        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleGenerate} className="generation-form">
          <div className="form-grid">
            <div className="form-column-left">
              <div className="form-group">
                <div className="prompt-header">
                  <label>Prompt *</label>
                  <div className="prompt-actions">
                    <button
                      type="button"
                      className="btn-copy-prompt-full"
                      onClick={handleCopyPrompt}
                      title="Copy prompt"
                    >
                      Copy
                    </button>
                    <button
                      type="button"
                      className="btn-erase-prompt"
                      onClick={() => setPrompt('')}
                      title="Clear prompt"
                    >
                      Erase
                    </button>
                  </div>
                </div>
                <textarea
                  ref={promptInputRef}
                  value={prompt}
                  onChange={handlePromptChange}
                  placeholder="Describe the image you want to generate..."
                  rows="12"
                  required
                  style={{ resize: 'none', overflow: 'hidden' }}
                />
              </div>

              <div className="form-group">
                <div className="prompt-templates-wrapper" ref={promptTemplatesRef}>
                  <button
                    type="button"
                    className="btn-prompt-templates"
                    onClick={() => setShowPromptTemplates(!showPromptTemplates)}
                  >
                    + Prompt Templates
                  </button>

                  {showPromptTemplates && (
                    <div className="prompt-templates-dropdown">
                      {promptTemplates.map((template) => (
                        <div key={template.id} className="template-item">
                          <button
                            type="button"
                            className="btn-template"
                            onClick={() => handleUseTemplate(template.text)}
                          >
                            {template.text}
                          </button>
                          <button
                            type="button"
                            className="btn-delete-template"
                            onClick={() => handleDeleteTemplate(template.id)}
                          >
                            ×
                          </button>
                        </div>
                      ))}

                      {isAddingTemplate ? (
                        <div className="add-template-form">
                          <input
                            type="text"
                            value={newTemplateText}
                            onChange={(e) => setNewTemplateText(e.target.value)}
                            placeholder="Enter template text..."
                            className="template-input"
                            autoFocus
                          />
                          <div className="template-actions">
                            <button
                              type="button"
                              className="btn-save-template"
                              onClick={handleAddTemplate}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              className="btn-cancel-template"
                              onClick={() => {
                                setIsAddingTemplate(false);
                                setNewTemplateText('');
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="btn-add-template"
                          onClick={() => setIsAddingTemplate(true)}
                        >
                          + Add a prompt
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {prefilledReferences.length > 0 && (
                <div className="form-group">
                  <label>Replicated References</label>
                  <div className="reference-scroll-row">
                    {prefilledReferences.map((ref, index) => (
                      <div key={`${ref.id}-${index}`} className="reference-thumbnail replicated">
                        <OptimizedImage src={ref.thumbnail_path || ref.path} alt={`Reference ${index + 1}`} />
                        <button
                          type="button"
                          className="delete-reference-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            removePrefilledReference(index);
                          }}
                          aria-label={`Remove reference ${index + 1}`}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!projectId && (
                <div className="form-group">
                  <label>One-Time Image References (Optional)</label>
                  <button
                    type="button"
                    className="btn-upload-reference"
                    onClick={handleClickOneTimeReference}
                  >
                    + Add One-Time Reference Image
                  </button>
                  <input
                    ref={oneTimeReferenceInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageReferenceChange}
                    style={{ display: 'none' }}
                  />
                  {imageReferencePreviews.length > 0 && (
                    <>
                      <div className="project-references-grid">
                        {imageReferencePreviews.map((preview, index) => (
                          <div
                            key={preview.url}
                            className={`reference-thumbnail ${preview.selected ? 'selected' : 'deselected'}`}
                            onClick={() => toggleImageReferenceSelection(index)}
                            style={{ cursor: 'pointer' }}
                          >
                            <OptimizedImage src={preview.url} alt={preview.name} eager />
                            <button
                              type="button"
                              className="delete-reference-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeImageReference(index);
                              }}
                              aria-label={`Remove ${preview.name}`}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                      <p className="file-count">
                        {imageReferencePreviews.filter(p => p.selected).length} of {imageReferences.length} file{imageReferences.length > 1 ? 's' : ''} selected
                      </p>
                    </>
                  )}
                  <p className="quantity-note">
                    Upload multiple images to use as references (up to 10 files)
                  </p>
                </div>
              )}
            </div>

            <div className="form-column-right">
              <div className="form-group">
                <label>Model</label>
                <div className="button-group model-button-group">
                  {IMAGE_MODEL_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`btn-option model-option ${generationModel === option.value ? 'selected' : ''}`}
                      onClick={() => setGenerationModel(option.value)}
                    >
                      <span className="model-option-title">{option.label}</span>
                      <span className="model-option-description">{option.description}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Aspect Ratio</label>
                <div className="button-group">
                  {aspectRatioOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`btn-option ${aspectRatio === option.label ? 'selected' : ''}`}
                      onClick={() => setAspectRatio(option.label)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Resolution</label>
                <div className="button-group">
                  <button
                    type="button"
                    className={`btn-option ${resolution === '1k' ? 'selected' : ''}`}
                    onClick={() => setResolution('1k')}
                  >
                    {`1K - $${getImageCost(generationModel, '1k').toFixed(2)}`}
                  </button>
                  <button
                    type="button"
                    className={`btn-option ${resolution === '2k' ? 'selected' : ''}`}
                    onClick={() => setResolution('2k')}
                  >
                    {`2K - $${getImageCost(generationModel, '2k').toFixed(2)}`}
                  </button>
                  <button
                    type="button"
                    className={`btn-option ${resolution === '4k' ? 'selected' : ''}`}
                    onClick={() => setResolution('4k')}
                  >
                    {`4K - $${getImageCost(generationModel, '4k').toFixed(2)}`}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Output Format</label>
                <div className="button-group">
                  <button
                    type="button"
                    className={`btn-option ${outputFormat === 'png' ? 'selected' : ''}`}
                    onClick={() => setOutputFormat('png')}
                  >
                    PNG
                  </button>
                  <button
                    type="button"
                    className={`btn-option ${outputFormat === 'jpg' ? 'selected' : ''}`}
                    onClick={() => setOutputFormat('jpg')}
                  >
                    JPG
                  </button>
                  <button
                    type="button"
                    className={`btn-option ${outputFormat === 'webp' ? 'selected' : ''}`}
                    onClick={() => setOutputFormat('webp')}
                  >
                    WebP
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Quantity</label>
                <div className="button-group">
                  <button
                    type="button"
                    className={`btn-option ${quantity === 1 ? 'selected' : ''}`}
                    onClick={() => setQuantity(1)}
                  >
                    1
                  </button>
                  <button
                    type="button"
                    className={`btn-option ${quantity === 2 ? 'selected' : ''}`}
                    onClick={() => setQuantity(2)}
                  >
                    2
                  </button>
                  <button
                    type="button"
                    className={`btn-option ${quantity === 4 ? 'selected' : ''}`}
                    onClick={() => setQuantity(4)}
                  >
                    4
                  </button>
                  <button
                    type="button"
                    className={`btn-option ${quantity === 6 ? 'selected' : ''}`}
                    onClick={() => setQuantity(6)}
                  >
                    6
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn-generate"
              disabled={isGenerating}
            >
              {isGenerating ? 'Generating...' : `Generate ${quantity > 1 ? quantity + ' Images' : 'Image'}`}
            </button>
          </div>
        </form>
      </div>

      {projectId && (
        <div className="reference-gallery-container">
          <div className="reference-gallery-header">
            <h3>Project References</h3>
          </div>
          <button
            type="button"
            className="btn-upload-reference"
            onClick={handleClickUploadReference}
          >
            + Add Image
          </button>
          <input
            ref={projectReferenceInputRef}
            type="file"
            accept="image/*"
            onChange={handleUploadProjectReference}
            style={{ display: 'none' }}
          />
          {projectReferences.length > 0 && (
            <div className="reference-gallery-list">
              {projectReferences.map((ref) => (
                <div
                  key={ref.id}
                  className={`reference-thumbnail-vertical ${selectedProjectReferences.includes(ref.id) ? 'selected' : 'deselected'}`}
                  onClick={() => handleToggleProjectReference(ref.id)}
                >
                  <OptimizedImage src={getPreviewImagePath(ref)} alt={ref.name} />
                  {ref.can_delete !== false && (
                    <button
                      type="button"
                      className="delete-reference-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteProjectReference(ref.id);
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GenerationMenu;
